import {useForm} from 'antd/es/form/Form';
import {
    useCallback, useEffect, useMemo, useRef, useState, Dispatch, SetStateAction,
    ReactNode,
} from 'react';

import {IModalOpenerComponentRef} from 'components/modal-opener-component';
import {resetLoadedData, selectEntityData} from 'modules/data';
import {actionRequestData} from 'modules/data/data-actions';
import {ITableEntityData, TEntity} from 'modules/data/data-types';
import {EEntityType} from 'shared/constants/entities';
import {ERequestType} from 'shared/types/requests';
import {showMessageFromResponse} from 'shared/utils';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';

import {fetchProjectInvestmentInfoItem} from '../api';
import {PROJECT_INVESTMENT_INFO_ITEM_MODAL_TAB_KEYS} from '../model/project-investment-info-item-modal.constants';
import {IDocumentDetails, IProjectInfluenceItemForm} from '../model/project-investment-info-item-modal.types';
import {convertResponseToProjectInfluenceItemForm} from '../utils';

interface IUseProjectInvestmentInfoItemModalArgs {
    entityName: string;
    wrapper?: ReactNode;
    record?: TEntity;
    tabKey?: string;
}

export const useProjectInvestmentInfoItemModal = ({
    entityName,
    record,
    tabKey,
}: IUseProjectInvestmentInfoItemModalArgs) => {
    const [activeTab, setActiveTab] = useState<string>(tabKey || PROJECT_INVESTMENT_INFO_ITEM_MODAL_TAB_KEYS.MAIN);
    const [form] = useForm<IProjectInfluenceItemForm>();
    const dispatch = useAppDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const modalRef = useRef<IModalOpenerComponentRef>(null);
    const tableEntityData = useAppSelector(
        selectEntityData(entityName, EEntityType.TABLE),
    ) as ITableEntityData & {selectedRows: IDocumentDetails[]};
    const selectedRows = useMemo(() => (
        tableEntityData && tableEntityData?.selectedRows ? tableEntityData?.selectedRows : []
    ),
    [tableEntityData]);
    const [isOpen, setIsOpen] = useState(false);

    const loadData = useCallback(async () => {
        if (!record?.id) {
            return;
        }

        try {
            const response = await fetchProjectInvestmentInfoItem({id: record.id as string});

            if (response?.status !== 200 || !response?.data) {
                return;
            }

            const formData: IProjectInfluenceItemForm = convertResponseToProjectInfluenceItemForm(response);
            form.setFieldsValue(formData);
        } catch (error) {
            showMessageFromResponse({response: error.response, isError: true});
        }
    }, [record, form]);

    useEffect(() => {
        if (!isOpen || !record?.id) {
            return;
        }

        setIsLoading(true);
        loadData().then(() => {
            setIsLoading(false);
        });
    }, [isOpen, record, loadData]);

    const handleFinish = useCallback(() => {
        actionRequestData({
            requestUrl: `ais3/investment.project/save`,
            requestType: ERequestType.POST,
            successMessage: 'Данные сохранены',
            options: {isJsonRequest: true},
            data: {
                ...form.getFieldsValue(true),
            },
        }).then((response:any) => {
            if (response?.status === 200) {
                modalRef.current?.setIsModalOpen(false);
                form.resetFields();
                dispatch(resetLoadedData(entityName, EEntityType.TABLE));
            }
        });
    }, [form, selectedRows, modalRef]);

    const handleCloseModal = useCallback((setIsModalOpen: Dispatch<SetStateAction<boolean>>) => {
        setActiveTab(PROJECT_INVESTMENT_INFO_ITEM_MODAL_TAB_KEYS.MAIN);
        form.resetFields();
        setIsModalOpen(false);
    }, [form]);

    return {
        modalRef,
        isOpen,
        form,
        isLoading,
        activeTab,
        setActiveTab,
        setIsOpen,
        handleCloseModal,
        handleFinish,
        loadData,
    };
};
